import { useLayoutEffect, useRef, useState } from "react";
import CodigoBarra from "../../../components/admin/CodigoBarra";
import { formatoFechaSimple } from "../../../utils/formatoFecha";
import "./EtiquetaProducto.css";

/**
 * Una etiqueta de codigo de barras, tal cual sale impresa.
 *
 * Vive aparte del modal de impresion porque la usan dos lugares: el modal
 * (para la vista previa y para el papel) y la ficha del producto (para
 * mostrar como va a quedar). Si estuviera dentro del modal, la ficha
 * tendria una copia, y la copia se atrasaria en cuanto la etiqueta cambie.
 *
 * Dos modos:
 *
 *   "inventario"  (por defecto) la de bodega: nombre, P/N, S/N, condicion,
 *                 fecha de certificado, cantidad en stock y ubicacion.
 *   "pedido"      la de una orden de venta: nombre, P/N, S/N, condicion,
 *                 certificado, la cantidad comprada (`producto.cantidad`) y
 *                 a que orden pertenece: numero de orden, PO y la posicion
 *                 de la linea (`linea` de `lineas`). La ubicacion no va:
 *                 describe donde estaba la pieza en bodega, no lo que se
 *                 despacha.
 */

export const MEDIDA_POR_DEFECTO = { ancho: 101, alto: 50 };

// Las barras y el texto escalan con el alto de la etiqueta: 7pt en una de
// 101 x 50 mm se ve diminuto y desperdicia el papel.
export const escalaTexto = (altoMm) =>
  altoMm >= 40 ? 1.55 : altoMm >= 30 ? 1.25 : 1;

const PX_POR_MM = 3.78; // 96 dpi

/* APAGADO A PEDIDO: el renglon de certificado (fecha y tipo) no se imprime
   por ahora. El codigo queda entero, alineado y probado; cuando se decida
   volver a mostrarlo alcanza con poner true aca. Se deja asi y no comentado
   para que el bloque siga compilando y no se pudra mientras esta apagado. */
const MOSTRAR_CERTIFICADO = false;

/**
 * Reparte el alto de la etiqueta entre los datos, el codigo y el logo.
 *
 * Antes el codigo se calculaba solo con el alto del papel y los datos se
 * llevaban lo que querian: con seis renglones al codigo le quedaban 25 px de
 * los 117 que necesita, salia aplastado y el numero de abajo se cortaba.
 * Ahora se mide lo que ocupan los datos y el codigo se queda con el resto.
 *
 * El codigo y el logo se calculan SIEMPRE con el reparto de la etiqueta de
 * producto, aunque la de pedido tenga menos renglones. Si se dejaran crecer
 * con el espacio sobrante, la misma pieza saldria con barras y logo de dos
 * tamanos distintos segun de donde se imprimio, y en un estante no se veria
 * como el mismo sistema. A la de pedido le queda aire, y esta bien.
 */
export const repartirAlto = ({ altoMm }) => {
  const escalaBase = escalaTexto(altoMm);
  const disponible = altoMm * PX_POR_MM - 9; // menos el padding

  // La escala de los datos: la de la etiqueta de producto, que es la mas
  // cargada, y la misma para las dos. Con cinco renglones el tamano grande
  // no deja lugar para el codigo, por eso el tope de 1.2. De aca salen
  // tambien el logo y el alto del codigo.
  const escalaRef = Math.min(escalaBase, 1.2);

  const altoFilaRef = 6.5 * 1.333 * escalaRef * 1.3 + 1.2;
  const altoNombreRef = 7 * 1.333 * escalaBase * 1.15;

  // Este 9 tiene que seguir al de .etq-logo img en el css: si se separan,
  // el reparto del alto queda mal y el codigo se corta.
  const altoLogo = 9 * PX_POR_MM * escalaRef;

  // Cinco renglones y el nombre: la etiqueta de producto.
  const altoDatosRef = Math.max(5 * altoFilaRef + altoNombreRef, altoLogo);

  // Lo que el SVG del codigo agrega ademas de las barras: los dos margenes,
  // la separacion del texto y el numero legible de abajo. Sale de las
  // opciones de CodigoBarra (margin 6 x2 + textMargin 4 + fontSize 14). Sin
  // descontarlo, el prop "alto" se interpreta como el total y el numero
  // termina cortado por el borde de la etiqueta.
  const EXTRA_SVG = 30;

  const paraBarras =
    disponible -
    altoDatosRef -
    // Holgura para los gap entre bloques y el redondeo de los mm a px.
    12 -
    EXTRA_SVG;

  return {
    escalaRef,
    barras: {
      alto: Math.round(Math.max(22, Math.min(paraBarras, 150))),
      // Barras mas anchas se leen mejor y aprovechan el ancho disponible.
      ancho: altoMm >= 40 ? 3 : altoMm >= 30 ? 1.8 : 1.4,
    },
  };
};

/**
 * Un renglon de la etiqueta: "P/N: 48919".
 *
 * Sin valor el renglon igual se imprime, con el espacio en blanco: sirve
 * para completarlo a mano, y ademas todas las etiquetas de la tanda salen
 * con la misma altura.
 */
const Campo = ({ et, valor }) => {
  const texto =
    valor === null || valor === undefined || valor === "" ? "" : String(valor);

  return (
    <div className="etq-campo">
      <span className="etq-campo-et">{et}:</span>
      <span className="etq-campo-valor">{texto}</span>
    </div>
  );
};

function EtiquetaProducto({
  producto,
  ancho = MEDIDA_POR_DEFECTO.ancho,
  alto = MEDIDA_POR_DEFECTO.alto,
  modo = "inventario",
}) {
  const esPedido = modo === "pedido";

  // Los dos modos entran en cinco renglones: el de inventario con Cert Date
  // y Location sueltos, el de pedido con los cuatro renglones dobles.
  const reparto = repartirAlto({ altoMm: alto });

  return (
    <div
      className="etq-etiqueta"
      style={{
        width: `${ancho}mm`,
        height: `${alto}mm`,
        "--etq-escala": escalaTexto(alto),
        "--etq-escala-datos": reparto.escalaRef,
        "--etq-escala-logo": reparto.escalaRef,
      }}
    >
      <div className="etq-contenido">
        {/* El logo va arriba a la derecha, al costado del bloque de datos:
            ahi no gasta alto propio y le deja mas lugar al codigo.
            El nombre entra dentro del mismo bloque para que el logo quede
            por encima de todo y no debajo del titulo. */}
        <div className="etq-cabecera">
          <div className="etq-datos">
            {producto.nombre ? (
              <div className="etq-nombre" title={producto.nombre}>
                {producto.nombre}
              </div>
            ) : null}

            {esPedido ? (
              <>
                {/* A la derecha, la orden a la que pertenece la pieza. Sin
                    esto una etiqueta despegada no se puede devolver a su
                    caja: el P/N dice que pieza es, no de que pedido. */}
                <div className="etq-par">
                  <Campo et="P/N" valor={producto.no_parte} />
                  <Campo et="SO" valor={producto.pedido_id} />
                </div>

                <div className="etq-par">
                  <Campo et="S/N" valor={producto.numero_serie} />
                  <Campo et="PO" valor={producto.no_po} />
                </div>

                <div className="etq-par">
                  <Campo et="Condition" valor={producto.condicion_nombre} />

                  {/* La cantidad comprada en la orden, no la que hay en
                      bodega. */}
                  <Campo et="Qty" valor={producto.cantidad ?? 0} />
                </div>

                {/* El certificado no va en media linea: "Form 8130-3 Dual
                    Release" no entra y se corta justo en la parte que lo
                    distingue de un 8130-3 normal. Se lleva el renglon y el
                    "Item" se acomoda a la derecha con lo que sobre. */}
                <div className="etq-par etq-par-fin">
                  <Campo et="Cert" valor={producto.certificado_nombre} />

                  {/* Le dice al que empaca si el envio esta completo. En una
                      orden de una sola linea no aporta nada. */}
                  {producto.lineas > 1 ? (
                    <Campo
                      et="Item"
                      valor={producto.linea + " of " + producto.lineas}
                    />
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <Campo et="P/N" valor={producto.no_parte} />

                <div className="etq-par etq-par--inv">
                  <Campo et="S/N" valor={producto.numero_serie} />
                  <Campo et="Condition" valor={producto.condicion_nombre} />
                </div>

                {/* Fecha y tipo de certificado en el mismo renglon, con el
                    tipo debajo de Condition.

                    Mismo reparto que la fila de arriba (etq-par--inv), para
                    que Cert arranque en la misma columna que Condition. La
                    derecha se lleva mas ancho porque ahi va el certificado:
                    "Form 8130-3 Dual Release" no entra en media linea y se
                    corta justo en la parte que lo distingue de un 8130-3
                    comun.

                    Si la pieza no tiene fecha, el renglon sale igual pero en
                    blanco, para completarlo a mano sobre la etiqueta. */}
                {MOSTRAR_CERTIFICADO ? (
                  <div className="etq-par etq-par--inv">
                    {/* "Date" y no "Cert Date": con el certificado al lado los
                        dos titulos completos no entran y la fecha se cortaba
                        en el ano, que es lo que hay que leer. Pegado a "Cert"
                        se entiende igual de que fecha se habla. */}
                    <Campo
                      et="Date"
                      valor={formatoFechaSimple(producto.fecha_certificado)}
                    />
                    <Campo et="Cert" valor={producto.certificado_nombre} />
                  </div>
                ) : null}

                {/* La cantidad no va en la etiqueta de una pieza: la pieza
                    es una, y el stock del numero de parte es justamente la
                    suma de sus piezas. Decir "Qty: 1" no agrega nada y
                    confunde con la cantidad del producto. */}
                {producto.es_unidad ? null : (
                  <Campo et="Qty" valor={producto.cantidad_disponible ?? 0} />
                )}

                <Campo et="Location" valor={producto.ubicacion} />
              </>
            )}
          </div>

          {/* El archivo tiene que venir recortado al ras: si trae margen
              transparente propio, la caja queda alineada al borde pero el
              dibujo aparece hundido y mas chico de lo que mide. Este ya esta
              recortado (2% de margen). */}
          <div className="etq-logo">
            <img src="/image/ADC-Logo.png" alt="" />
          </div>
        </div>

        <div className="etq-barras">
          <CodigoBarra
            valor={producto.codigo_barra}
            alto={reparto.barras.alto}
            ancho={reparto.barras.ancho}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * La misma etiqueta, achicada para que entre donde se la ponga.
 *
 * Se dibuja al tamano real y se encoge entera con transform, en vez de
 * redibujarla mas chica: asi lo que se ve en pantalla es exactamente lo que
 * sale del papel, y una etiqueta que cambie no deja una vista previa vieja
 * mostrando otra cosa.
 */
export function EtiquetaPreview({ producto, ancho, alto, modo }) {
  const marco = useRef(null);
  const [escala, setEscala] = useState(null);

  const anchoMm = ancho ?? MEDIDA_POR_DEFECTO.ancho;
  const altoMm = alto ?? MEDIDA_POR_DEFECTO.alto;

  // Los mm del css son exactos: 96 px por pulgada, siempre. Se calcula el
  // ancho real asi y no midiendo el elemento, porque una vez escalado lo
  // que se mide ya viene encogido y la cuenta se muerde la cola.
  const anchoRealPx = (anchoMm * 96) / 25.4;
  const altoRealPx = (altoMm * 96) / 25.4;

  useLayoutEffect(() => {
    const el = marco.current;
    if (!el) return;

    const medir = () => {
      const disponible = el.clientWidth;
      if (disponible > 0) setEscala(Math.min(1, disponible / anchoRealPx));
    };

    medir();

    // La ficha del producto cambia de columnas segun el ancho de la
    // ventana, asi que el hueco no es fijo y hay que volver a medirlo.
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [anchoRealPx]);

  return (
    <div className="etq-preview-marco" ref={marco}>
      <div
        className="etq-preview-lienzo"
        style={{
          // Antes de la primera medida se deja el alto real: si se pusiera
          // cero, el hueco saltaria de golpe al aparecer la etiqueta.
          height: `${Math.round(altoRealPx * (escala ?? 1))}px`,
          // Y se esconde hasta tener la escala, para que no se vea un
          // parpadeo con la etiqueta a tamano real.
          visibility: escala === null ? "hidden" : "visible",
        }}
      >
        <div
          className="etq-preview-escala"
          style={{ transform: `scale(${escala ?? 1})` }}
        >
          <EtiquetaProducto
            producto={producto}
            ancho={anchoMm}
            alto={altoMm}
            modo={modo}
          />
        </div>
      </div>
    </div>
  );
}

export default EtiquetaProducto;
